import { useCallback, useEffect, useState } from "react";
import { api, flash, post } from "../lib/api";
import { dayLabel } from "../lib/time";
import { addDays, dayOff, shortDate, weekday } from "../lib/days";
import { Sheet } from "./bits";

function NextList({ cal }) {
  const skipped = [];
  for (let d = addDays(cal.today, 1); d < cal.tomorrow; d = addDays(d, 1)) skipped.push(`${dayLabel(d)} (${dayOff(d, cal) || "day off"})`);
  return (
    <div className="cal-next" data-testid="cal-next-list">
      <b>Next list: {dayLabel(cal.tomorrow)}</b>
      {skipped.length > 0 && <p>No list for {skipped.join(", ")}.</p>}
      <p>Lists are packed only for working days: Monday to Friday, minus CK holidays.</p>
    </div>
  );
}

function WorkDay({ cal, people, pick, reload }) {
  const existing = cal.workingDays.find((w) => w.date === pick);
  const [ticked, setTicked] = useState(new Set(existing?.people || []));
  const [note, setNote] = useState(existing?.note || "");
  const [err, setErr] = useState("");
  const flip = (id) => { const s = new Set(ticked); s.has(id) ? s.delete(id) : s.add(id); setTicked(s); };
  async function save() {
    const r = await post("/calendar/working-days", { date: pick, people: [...ticked], note, rev: cal.rev });
    if (!r.ok) return setErr(r.error.message);
    flash(r.data.message); reload();
  }
  async function undo() {
    const r = await api(`/calendar/working-days/${pick}`, { method: "DELETE" });
    if (!r.ok) return setErr(r.error.message);
    flash(r.data.message); reload();
  }
  return (
    <div className="cal-who" data-testid="cal-who">
      <div className="cal-who-head"><b>Who is in on {dayLabel(pick)}?</b>
        <button className="link sm hit" onClick={() => setTicked(new Set(people.map((p) => p.id)))} data-testid="cal-everyone">Everyone</button>
        <button className="link sm hit" onClick={() => setTicked(new Set())} data-testid="cal-nobody">Nobody</button></div>
      {["design", "video"].map((team) => (
        <div key={team} className="cal-team" data-team={team}>
          <div className="lbl">{team === "design" ? "Design" : "Video"}</div>
          {people.filter((p) => p.team === team).map((p) => (
            <label key={p.id} className="check sm"><input type="checkbox" checked={ticked.has(p.id)} onChange={() => flip(p.id)} data-testid={`cal-person-${p.id}`} /> {p.short}</label>
          ))}
        </div>
      ))}
      <input className="fld" value={note} maxLength={140} placeholder="Why, e.g. Bajaj launch" aria-label="Why this is a working day"
        onChange={(e) => setNote(e.target.value)} data-testid="cal-note" />
      {err && <p className="err" role="alert" data-testid="cal-error">{err}</p>}
      <div className="cs-actions">
        <button className="btn-solid" disabled={!ticked.size} onClick={save} data-testid="cal-make-working">Make it a working day for {ticked.size}</button>
        {existing && <button className="btn-ghost sm danger" onClick={undo} data-testid="cal-back-to-off">Back to a day off</button>}
      </div>
    </div>
  );
}

function WorkingDays({ cal, people, reload }) {
  const [pick, setPick] = useState(null);
  const days = [];
  for (let i = 0; i < 35 && days.length < 14; i++) {
    const d = addDays(cal.today, i);
    const w = cal.workingDays.find((x) => x.date === d);
    const off = dayOff(d, cal);
    if (w || off) days.push({ d, why: w ? "Working day" : off });
  }
  const short = Object.fromEntries(people.map((p) => [p.id, p.short]));
  return (
    <section className="cal-sec">
      <div className="lbl">Work on a day off</div>
      <p className="hint">For a working Saturday (or a holiday): pick the day and who is coming in. The day before packs it, and it then packs the next working day. Everyone not ticked is off that day, so no cards land on them.</p>
      <div className="cal-days">
        {days.map(({ d, why }) => (
          <button key={d} className={`cal-day ${pick === d ? "on" : ""}`} aria-pressed={pick === d} onClick={() => setPick(d)} data-testid={`cal-day-${d}`}>
            <b>{dayLabel(d)}</b><span>{why}</span></button>
        ))}
      </div>
      {pick && <WorkDay key={pick} cal={cal} people={people} pick={pick} reload={() => { setPick(null); reload(); }} />}
      {cal.workingDays.filter((w) => w.date >= cal.today).map((w) => (
        <p key={w.date} className="cal-wline" data-testid={`cal-working-${w.date}`}><span className="tag">Working</span>
          <b>{dayLabel(w.date)}</b>: {w.people.map((p) => short[p] || p).join(", ")}{w.note ? ` (${w.note})` : ""}</p>
      ))}
    </section>
  );
}

function HolidayForm({ edit, today, onSaved, onCancel }) {
  const [f, setF] = useState({ date: edit?.date || "", name: edit?.name || "" });
  const [err, setErr] = useState("");
  async function save(e) {
    e.preventDefault();
    const r = await post("/calendar/holidays", { date: f.date, name: f.name.trim(), oldDate: edit?.date || null, tentative: false });
    if (!r.ok) return setErr(r.error.message);
    flash(r.data.message); onSaved();
  }
  return (
    <form className="cal-hform" onSubmit={save} data-testid="holiday-form">
      <input className="fld" type="date" min={addDays(today, 1)} value={f.date} aria-label="Date" onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="holiday-date" />
      <input className="fld" maxLength={60} placeholder="e.g. Diwali" aria-label="Holiday" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="holiday-name" />
      <button className="btn-solid" disabled={!f.date || !f.name.trim()} data-testid="holiday-save">{edit ? "Save the change" : "Add holiday"}</button>
      {edit && <button type="button" className="btn-ghost sm" onClick={onCancel} data-testid="holiday-cancel">Cancel</button>}
      {err && <p className="err" role="alert" data-testid="holiday-error">{err}</p>}
    </form>
  );
}

function Holidays({ cal, canEdit, reload }) {
  const [edit, setEdit] = useState(null);
  const year = cal.today.slice(0, 4);
  const shown = cal.holidays.filter((h) => h.date.startsWith(year) || h.date > cal.today);
  const years = [...new Set(shown.map((h) => h.date.slice(0, 4)))].sort();
  async function remove(h) {
    const r = await api(`/calendar/holidays/${h.date}`, { method: "DELETE" });
    flash(r.ok ? r.data.message : r.error.message);
    if (r.ok) reload();
  }
  return (
    <section className="cal-sec" data-testid="cal-holidays">
      <div className="cal-hhead"><div className="lbl">CK holidays</div><span className="hint">{canEdit ? "Management edits these." : "Set by management."}</span></div>
      {cal.holidayReminder && <p className="cal-remind" role="status" data-testid="holiday-reminder"><b aria-hidden="true">!</b> {cal.holidayReminder}</p>}
      {years.map((y) => (
        <div key={y} className="cal-year" data-testid={`holiday-year-${y}`}>
          <div className="cal-y">{y}</div>
          {shown.filter((h) => h.date.startsWith(y)).map((h) => (
            <div key={h.date} className={`cal-hrow ${h.date <= cal.today ? "past" : ""}`} data-testid={`holiday-${h.date}`}>
              <span className="mono">{shortDate(h.date)}</span><span className="cal-wd">{weekday(h.date).slice(0, 3)}</span>
              <span className="cal-hn">{h.name}{h.tentative && <em className="cal-move"> date may move</em>}</span>
              {canEdit && h.date > cal.today && <>
                <button className="link sm hit" onClick={() => setEdit(h)} data-testid={`holiday-change-${h.date}`}>Change</button>
                <button className="link sm hit danger-t" onClick={() => remove(h)} data-testid={`holiday-remove-${h.date}`}>Remove</button></>}
            </div>
          ))}
        </div>
      ))}
      {canEdit && <HolidayForm key={edit?.date || "new"} edit={edit} today={cal.today} onCancel={() => setEdit(null)} onSaved={() => { setEdit(null); reload(); }} />}
    </section>
  );
}

export function CalendarSheet({ me, onClose, onChanged }) {
  const [cal, setCal] = useState(null);
  const [people, setPeople] = useState([]);
  const load = useCallback(async () => { const r = await api("/calendar"); if (r.ok) setCal(r.data); }, []);
  useEffect(() => {
    load();
    api("/people?bench=true").then((r) => r.ok && setPeople(r.data.items.filter((p) => p.active && !p.leftAt && ["design", "video"].includes(p.team))));
  }, [load]);
  const reload = () => { load(); onChanged(); };
  return (
    <Sheet title="Working days" onClose={onClose} testid="calendar-sheet" width={560}>
      {!cal ? <p className="loading">Loading the calendar…</p> : <>
        <NextList cal={cal} />
        <WorkingDays cal={cal} people={people} reload={reload} />
        <Holidays cal={cal} canEdit={me.caps.offboard} reload={reload} />
        <p className="hint mt">Cards already on a day that becomes a holiday move to the next working day's request tray.</p>
      </>}
    </Sheet>
  );
}
